import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, Link } from "react-router-dom";
import Loader from "../../components/common/Loader";
import { ArrowLeft } from "lucide-react";
import { Modal } from "../../components/ui/modal";

interface Product {
  id: string;
  prod_name: string;
  prod_type: string;
  prod_cat_name: string;
  subcat_name: string;
  prod_part_name: string;
  prod_part_number: string;
  prod_cost: string;
  prod_price: string;
  SGST: string;
  CGST: string;
  status: string;
  prod_url: string;
  prod_desc: string;
  prod_img1?: string;
  prod_img2?: string;
  prod_img3?: string;
  effective_date?: string;
}

const ProductView: React.FC = () => {
  const [product, setProduct] = useState<Product | null>(null);
  const [errors, setErrors] = useState<{ prod_name?: string }>({});
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { id: productId } = useParams();

  useEffect(() => {
    fetchProductDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProductDetails = async () => {
    const token = sessionStorage.getItem("access_token");

    if (!token) {
      setErrors({ prod_name: "Authorization required. Please log in." });
      navigate("/signin");
      return;
    }

    if (!productId) {
      setErrors({ prod_name: "Product ID missing in the URL" });
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/product/show`,
        { id: productId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 200) {
        setProduct(response.data.data);
      } else {
        console.error("Failed to fetch product details:", response.data.message);
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  const openModal = (image: string) => {
    setSelectedImage(image);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  return (
    <div
      className="max-w-7xl mx-auto text-gray-800 dark:text-gray-100"
      style={{
        fontFamily: "Lato, sans-serif",
        fontSize: "12px",
        fontWeight: "normal",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap"
        rel="stylesheet"
      />

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <div className="flex items-center px-0 py-3 justify-between">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white/90 text-center">
            Product Details
          </h3>
          <Link
            to="/product-list"
            className="px-3 py-1.5 bg-[#2f6aa7] dark:bg-[#3d6794] hover:bg-[#2779bd] dark:hover:bg-[#335877] text-sm text-white dark:text-gray-300 rounded-lg flex items-center"
          >
            <ArrowLeft className="inline-block mr-1" size={16} />
            Back
          </Link>
        </div>

        <div className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-600 shadow-sm p-4 rounded-lg transition-colors duration-300">
          {errors.prod_name ? (
            <div className="text-red-500 font-bold text-[14px]">{errors.prod_name}</div>
          ) : product ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  Product Name
                </strong>
                <p className="text-theme-sm text-black dark:text-white">
                  {product.prod_name || "-"}
                </p>
              </div>

              <div>
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  Part Name
                </strong>
                <p className="text-[12px]">{product.prod_part_name || "-"}</p>
              </div>

              <div>
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  Part Number
                </strong>
                <p className="text-[12px]">{product.prod_part_number || "-"}</p>
              </div>

              <div>
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  Product Type
                </strong>
                <p className="text-[12px]">{product.prod_type || "-"}</p>
              </div>

              <div>
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  Price
                </strong>
                <p className="text-[12px]">
                  ₹{parseFloat(product.prod_price).toLocaleString() || "-"}
                </p>
              </div>

              <div>
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  Cost
                </strong>
                <p className="text-[12px]">
                  ₹{parseFloat(product.prod_cost).toLocaleString() || "-"}
                </p>
              </div>

              <div>
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  Category
                </strong>
                <p className="text-[12px]">{product.prod_cat_name || "-"}</p>
              </div>

              <div>
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  Subcategory
                </strong>
                <p className="text-[12px]">{product.subcat_name || "-"}</p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                    SGST
                  </strong>
                  <p className="text-[12px]">{parseFloat(product.SGST) || "-"}%</p>
                </div>
                <div>
                  <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                    CGST
                  </strong>
                  <p className="text-[12px]">{parseFloat(product.CGST) || "-"}%</p>
                </div>
              </div>

              <div>
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  URL
                </strong>
                <p className="text-[12px]">
                  <a
                    href={product.prod_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 underline break-words"
                  >
                    {product.prod_url || "-"}
                  </a>
                </p>
              </div>

              <div>
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  Effective Date
                </strong>
                <p className="text-[12px]">{product.effective_date || "-"}</p>
              </div>

              <div>
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  Status
                </strong>
                <p
                  className={`text-[12px] ${
                    product.status === "Active"
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-600 dark:text-red-400"
                  }`}
                >
                  <span className="inline-flex items-center">
                    <svg className="w-2 h-2 mr-1" fill="currentColor" viewBox="0 0 8 8">
                      <circle cx="4" cy="4" r="3" />
                    </svg>
                    {product.status}
                  </span>
                </p>
              </div>

              <div className="sm:col-span-3">
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  Description
                </strong>
                <p className="text-[12px] text-justify">{product.prod_desc || "-"}</p>
              </div>

              <div className="sm:col-span-3">
                <strong className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  Product Images
                </strong>
                <div className="flex flex-wrap gap-4 mt-2">
                  {product.prod_img1 && (
                    <img
                      src={product.prod_img1}
                      alt={product.prod_name || "Product preview 1"}
                      className="rounded border object-contain cursor-pointer hover:opacity-90 transition-opacity"
                      style={{ width: "200px", height: "150px" }}
                      onClick={() => openModal(product.prod_img1!)}
                    />
                  )}
                  {product.prod_img2 && (
                    <img
                      src={product.prod_img2}
                      alt={product.prod_name ? `${product.prod_name} alternate view` : "Alternate product view"}
                      className="rounded border object-contain cursor-pointer hover:opacity-90 transition-opacity"
                      style={{ width: "200px", height: "150px" }}
                      onClick={() => openModal(product.prod_img2!)}
                    />
                  )}
                  {product.prod_img3 && (
                    <img
                      src={product.prod_img3}
                      alt={product.prod_name ? `${product.prod_name} additional view` : "Additional product view"}
                      className="rounded border object-contain cursor-pointer hover:opacity-90 transition-opacity"
                      style={{ width: "200px", height: "150px" }}
                      onClick={() => openModal(product.prod_img3!)}
                    />
                  )}
                  {!product.prod_img1 && !product.prod_img2 && !product.prod_img3 && (
                    <span className="font-normal text-gray-500 dark:text-gray-400">
                      No images available
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Loader />
          )}
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <Modal isOpen={!!selectedImage} onClose={closeModal} className="max-w-4xl p-4">
          <div className="flex justify-center items-center h-full">
            <img
              src={selectedImage}
              alt="Full-size product view"
              className="w-[600px] h-[400px] object-contain rounded-lg"
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ProductView;
